/* eslint-disable react/no-unstable-nested-components */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { DataGrid } from '../../components/datagrid';
import { SearchFilter } from '../../components/inputs/filters/search';
import { PageTitle } from '../../components/PageTitle';
import { useFetch } from '../../hooks/useFetch';
import { getCellStatus } from '../../utils/FormatFunctions';
import { useAuth } from '../../providers/auth';
import { api } from '../../services/api';
import {
  Alert,
  AlertDescription,
  FramedCard,
  FramedCardContent,
  FramedCardHeader,
  KpiStrip,
} from '../../components/ui';
import { DataSkeleton } from '../../components/states';

interface TemplatesSummary {
  institutionsTotal: number;
  institutionsManual: number;
  institutionsDefault: number;
  institutionsWithoutTemplates: number;
  extraMaterialsTotal: number;
  topicsTotal: number;
  modulesTotal: number;
  categoriesTotal: number;
}

export function TemplatesPage() {
  const baseApiUrl = '/v1/admin';
  const baseUrl = `${baseApiUrl}/extra-material-templates`;
  const { requestApi, data, loading, error } = useFetch(baseUrl);
  const { user } = useAuth();
  const [summary, setSummary] = useState<TemplatesSummary>({
    institutionsTotal: 0,
    institutionsManual: 0,
    institutionsDefault: 0,
    institutionsWithoutTemplates: 0,
    extraMaterialsTotal: 0,
    topicsTotal: 0,
    modulesTotal: 0,
    categoriesTotal: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const columns = [
    { name: 'id', label: 'Id' },
    { name: 'name', label: 'Nome' },
    { name: 'type', label: 'Tipo' },
    { name: 'user_type', label: 'Material para' },
    { name: 'school_class.name', label: 'Turma' },
    { name: 'category.name', label: 'Categoria' },
    { name: 'is_official', label: 'Oficial' },
    { name: 'version', label: 'Versão' },
    { name: 'status', label: 'Status' },
  ];

  const groupIds = user?.group?.id;

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const response = await api.get('/v1/admin/templates/summary');
      const payload = response.data?.data ?? {};
      setSummary({
        institutionsTotal: payload.institutions_total ?? 0,
        institutionsManual: payload.institutions_manual ?? 0,
        institutionsDefault: payload.institutions_default ?? 0,
        institutionsWithoutTemplates: payload.institutions_without_templates ?? 0,
        extraMaterialsTotal: payload.extra_materials_total ?? 0,
        topicsTotal: payload.topics_total ?? 0,
        modulesTotal: payload.modules_total ?? 0,
        categoriesTotal: payload.categories_total ?? 0,
      });
    } catch (err: any) {
      setSummaryError(
        err?.response?.data?.msg ?? 'Não foi possível carregar o resumo dos templates.'
      );
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const institutionKpis = useMemo(
    () => [
      {
        id: 'institutionsTotal',
        label: 'Instituições',
        value: summary.institutionsTotal.toLocaleString('pt-BR'),
        hint: 'Cadastradas na plataforma',
      },
      {
        id: 'institutionsDefault',
        label: 'Com template padrão',
        value: summary.institutionsDefault.toLocaleString('pt-BR'),
        hint: 'Materiais oficiais pré-configurados',
      },
      {
        id: 'institutionsManual',
        label: 'Com template personalizado',
        value: summary.institutionsManual.toLocaleString('pt-BR'),
        hint: 'Escolhem quais materiais disponibilizar',
      },
      {
        id: 'institutionsWithoutTemplates',
        label: 'Sem template vinculado',
        value: summary.institutionsWithoutTemplates.toLocaleString('pt-BR'),
        hint: 'Precisam de configuração',
      },
    ],
    [summary]
  );

  const contentKpis = useMemo(
    () => [
      {
        id: 'extraMaterials',
        label: 'Materiais extras',
        value: summary.extraMaterialsTotal.toLocaleString('pt-BR'),
        hint: 'PDFs, vídeos e links nos templates',
      },
      {
        id: 'topics',
        label: 'Tópicos',
        value: summary.topicsTotal.toLocaleString('pt-BR'),
        hint: 'Assuntos que organizam os materiais',
      },
      {
        id: 'modules',
        label: 'Módulos',
        value: summary.modulesTotal.toLocaleString('pt-BR'),
        hint: 'Agrupamentos pedagógicos',
      },
      {
        id: 'categories',
        label: 'Categorias',
        value: summary.categoriesTotal.toLocaleString('pt-BR'),
        hint: 'Classificações por tipo ou finalidade',
      },
    ],
    [summary]
  );

  return (
    <Box>
      <PageTitle description="Gerencie os modelos de conteúdo usados pelas instituições.">
        Gestão de Templates
      </PageTitle>

      {summaryError && (
        <Alert variant="error" mb="16px">
          <AlertDescription>{summaryError}</AlertDescription>
        </Alert>
      )}
      {!summaryError && summaryLoading && (
        <DataSkeleton variant="cards" rows={4} label="Carregando resumo dos templates" />
      )}
      {!summaryError && !summaryLoading && (
        <Flex direction="column" gap="16px" mb="16px">
          <FramedCard role="region" aria-label="Instituições">
            <FramedCardHeader px="16px" py="8px">
              <Text fontSize="13px" fontWeight={500} color="content.strong">
                Instituições
              </Text>
            </FramedCardHeader>
            <FramedCardContent p={0}>
              <KpiStrip items={institutionKpis} />
            </FramedCardContent>
          </FramedCard>
          <FramedCard role="region" aria-label="Conteúdos">
            <FramedCardHeader px="16px" py="8px">
              <Text fontSize="13px" fontWeight={500} color="content.strong">
                Conteúdos nos templates
              </Text>
            </FramedCardHeader>
            <FramedCardContent p={0}>
              <KpiStrip items={contentKpis} />
            </FramedCardContent>
          </FramedCard>
        </Flex>
      )}

      <DataGrid
        columns={columns}
        rows={data?.data}
        requestApi={requestApi}
        error={error}
        baseUrl={baseUrl}
        data={data}
        hasCreate={groupIds === 1} // Only Sistema group can create templates
        loading={loading}
        rowActions={{
          show: false,
          edit: groupIds === 1, // Only Sistema group can edit
          delete: groupIds === 1, // Only Sistema group can delete
        }}
        formatColum={[
          {
            field: 'status',
            formatField: getCellStatus,
          },
          {
            field: 'is_official',
            formatField: (value: boolean) => (
              <Badge variant={value ? 'success' : 'neutral'}>{value ? 'Sim' : 'Não'}</Badge>
            ),
          },
          {
            field: 'user_type',
            formatField: (value: string) => {
              const userTypes: Record<string, string> = {
                TEACHER: 'Professor',
                STUDENT: 'Aluno',
                TODOS: 'Todos',
              };
              return userTypes[value] || value;
            },
          },
        ]}
        headerFilters={[
          {
            name: 'search',
            filterComponent: SearchFilter,
          },
        ]}
      />
    </Box>
  );
}
